import React, { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';
import { DropDown, useFocusEventHandler, useSearchState, Option, activateOnKey } from './index';
import { CalendarResource } from 'types';

interface CalendarResourceFieldProps {
  onChange?: Function;
  value?: number;
  jobType?: number;
  trade?: number|null;
  warehouseId?: number;
  disabled?: boolean;
}

interface SearchResultsProps {
  isFocused: boolean;
  jobType?: number;
  trade?: number|null;
  warehouseId?: number;
  handleClick: Function;
  handleOptionSelected: Function;
  searchFilter: string;
  value?: number;
}

const GET_CALENDAR_RESOURCES = gql`
  query getCalendarResources($jobType: ID!, $trade: ID!, $warehouseId: ID!, $search: String) {
    getCalendarResources(filter: {
      jobType: $jobType,
      trade: $trade,
      warehouseId: $warehouseId,
      search: $search,
    }) {
      id
      name
      isActive
    }
  }
`;

const CalendarResourceFieldWrapper = styled.div`
  position: relative;

  ${DropDown} {
    max-height: 200px;
  }
`;

function parseResponse(data: any): Option[] {
  let calendarResources = data ? data.getCalendarResources.map((option: CalendarResource) => {
    return { value: option.id, label: option.name }
  }) : [];

  if (calendarResources.length > 0) {
    calendarResources.unshift({ value: null, label: 'Unassigned' });
  }

  return calendarResources;
}

function SearchResults({
  isFocused,
  handleClick,
  handleOptionSelected,
  jobType,
  trade,
  warehouseId,
  searchFilter,
  value,
}: SearchResultsProps) {
  const { loading, data } = useQuery(GET_CALENDAR_RESOURCES, {
    skip: !jobType || !trade || !warehouseId,
    variables: { jobType: jobType, trade: trade, warehouseId: warehouseId, search: searchFilter },
    onCompleted: (data) => {
      if (searchFilter === "") {
        const comparisonValue = (value) ? value : null;
        const optionToSelect = parseResponse(data).find((option) => option.value === comparisonValue);
        handleOptionSelected(optionToSelect?.label);
      }
    }
  });

  return (
    <>
      {(!loading && isFocused) &&
        <DropDown role="listbox">
          {parseResponse(data).map((option: Option) => (
            <li role="option" tabIndex={0} onMouseDown={(e) => e.preventDefault()} onClick={() => handleClick(option)} onKeyDown={activateOnKey(() => handleClick(option))} key={option.value}>{option.label}</li>
          ))}
        </DropDown>
      }
    </>
  );
}

function CalendarResourceField({
  onChange,
  jobType,
  trade,
  warehouseId,
  value,
  disabled = false,
}: CalendarResourceFieldProps) {
  const { searchFilter, searchValue, handleOptionSelected, onSearchChange } = useSearchState('');
  const { isFocused, setIsFocused, handleFocusEvent } = useFocusEventHandler();
  const [isInit, setIsInit] = useState(true);

  useEffect(() => {
    if (!isInit) {
      onChange && onChange(null);
      handleOptionSelected('Unassigned');
    }
    setIsInit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade, warehouseId])

  const handleClick = (option: Option): void => {
    handleOptionSelected(option?.label);
    setIsFocused(false);
    onChange && onChange(option?.value);
  }

  return (
    <CalendarResourceFieldWrapper>
      <input
        id='calendar-resource-field'
        value={searchValue}
        onChange={onSearchChange}
        onFocus={handleFocusEvent}
        onBlur={isMobile ? handleFocusEvent : undefined}
        autoComplete='off'
        disabled={disabled || (!jobType && !trade && !warehouseId)}
      />
      <SearchResults
        isFocused={isFocused}
        searchFilter={searchFilter}
        handleClick={handleClick}
        handleOptionSelected={handleOptionSelected}
        value={value}
        jobType={jobType}
        trade={trade}
        warehouseId={warehouseId}
      />
    </CalendarResourceFieldWrapper>
  );
}

export default CalendarResourceField;
